import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { niciApi } from '../../services/api'
import { ClipboardList, Calculator, TrendingUp, AlertTriangle } from 'lucide-react'

interface DashboardData {
  pratiche_per_stato: Record<string, number>
  offerte_per_stato: Record<string, number>
  margine_medio_30gg: number | null
  pratiche_senza_offerta: { id: string; codice: string; ragione_sociale: string; stato: string }[]
}

function Tile({ icon: Icon, label, value, color }: { icon: React.ElementType; label: string; value: string | number; color: string }) {
  return (
    <div className="card p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}20` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-lg font-semibold text-slate-900">{value}</p>
      </div>
    </div>
  )
}

export default function NiciDashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['nici-dashboard'],
    queryFn: () => niciApi.dashboard().then(r => r.data as DashboardData),
  })

  if (isLoading || !data) return <div className="card p-8 text-center text-sm text-slate-400">Caricamento...</div>

  const totalePratiche = Object.values(data.pratiche_per_stato).reduce((a, b) => a + b, 0)
  const totaleOfferte = Object.values(data.offerte_per_stato).reduce((a, b) => a + b, 0)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">Dashboard NICI</h1>
        <p className="page-subtitle">Panoramica pratiche di sopralluogo e offerte</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile icon={ClipboardList} label="Pratiche totali" value={totalePratiche} color="#1E40AF" />
        <Tile icon={Calculator} label="Offerte totali" value={totaleOfferte} color="#7c3aed" />
        {data.margine_medio_30gg !== null && (
          <Tile icon={TrendingUp} label="Margine medio 30gg" value={`${data.margine_medio_30gg.toFixed(1)}%`} color="#15803d" />
        )}
        <Tile icon={AlertTriangle} label="Pratiche senza offerta" value={data.pratiche_senza_offerta.length} color="#f59e0b" />
      </div>

      {data.pratiche_senza_offerta.length > 0 && (
        <div className="card p-4 space-y-2">
          <h3 className="font-semibold text-sm">Pratiche compilate senza offerta</h3>
          {data.pratiche_senza_offerta.map(p => (
            <div key={p.id} className="flex items-center justify-between border-b border-slate-50 py-1.5 text-sm">
              <span>{p.codice} — {p.ragione_sociale}</span>
              <Link to="/nici/offerte" className="text-xs" style={{ color: '#1E40AF' }}>Genera offerta →</Link>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
